/**
 * v5 top bar — brand mark, session live pill, connection + participant
 * pill, and current user pill. Replaces the old
 * `<header class="app-header"><h1>Arcane Circle VTT</h1>...</header>`
 * with a 56-px chrome strip from the v5 design system.
 *
 * Presentational only — receives connection state + auth from App.tsx.
 */

import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'

interface TopBarProps {
  displayName: string
  role: 'gm' | 'player' | 'observer'
  connected: boolean
  participantCount: number
  participantMax?: number
  /** Optional game title — shown in the session breadcrumb when the
   *  metadata fetch has resolved. Falls back to a generic "Session"
   *  label until then. */
  gameTitle?: string
  /** Optional session ordinal (e.g. 3 → "Session 3"). */
  sessionNumber?: number
  /** Mobile sidebar drawer state — App.tsx owns it; the hamburger
   *  rendered here toggles. Desktop hides the hamburger via CSS. */
  sidebarOpen?: boolean
  onToggleSidebar?: () => void
}

export function TopBar({
  displayName,
  role,
  connected,
  participantCount,
  participantMax,
  gameTitle,
  sessionNumber,
  sidebarOpen = false,
  onToggleSidebar,
}: TopBarProps) {
  const initial = displayName.charAt(0).toUpperCase()
  const isGM = role === 'gm'

  return (
    <header className="top-bar">
      {/* Mobile sidebar toggle — hidden on desktop via CSS. Sized
          for thumb reach (44px hit target). Icon flips between
          hamburger and × based on drawer state. */}
      {onToggleSidebar && (
        <IconButton
          variant="ghost"
          className="vtt-mobile-toggle"
          aria-label={sidebarOpen ? 'Close session menu' : 'Open session menu'}
          aria-expanded={sidebarOpen}
          onClick={onToggleSidebar}
        >
          <span aria-hidden style={{ fontSize: 20, lineHeight: 1 }}>
            {sidebarOpen ? '×' : '☰'}
          </span>
        </IconButton>
      )}

      {/* Brand mark */}
      <div
        className="flex items-center gap-2.5 flex-shrink-0"
        style={{ marginRight: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.625rem' }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            className="font-script"
            style={{
              fontSize: '0.5625rem',
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              lineHeight: 1,
              color: 'hsl(var(--ac-mint-primary))',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 280,
            }}
            title={gameTitle ?? 'Arcane Circle'}
          >
            {gameTitle ?? 'Arcane Circle'}
          </div>
          <div
            className="font-serif-ac"
            style={{
              fontSize: '0.9375rem',
              lineHeight: 1,
              marginTop: 3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 280,
            }}
            title={sessionNumber != null ? `Session ${sessionNumber}` : 'Session'}
          >
            {sessionNumber != null ? `Session ${sessionNumber}` : 'Session'}
          </div>
        </div>
      </div>

      {/* Session live pill */}
      <div
        className="vtt-topbar-live"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          paddingLeft: '1rem',
          marginLeft: '0.5rem',
          borderLeft: '1px solid hsl(var(--ac-dark-accent))',
          minWidth: 0,
        }}
      >
        {/* Status pill — semantically a label, not a button (no onClick).
            Was previously wrapped in `ac-segment > button.is-selected` which
            ships a white active fill; rebuilt as a Grimoire-token-styled
            badge so it reads mint-ish-tinted instead of white. */}
        <div
          role="status"
          aria-live="polite"
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            height: 28,
            padding: '0 0.75rem',
            borderRadius: 'var(--ac-radius-full)',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: connected
              ? 'hsl(var(--ac-mint-primary))'
              : 'hsl(var(--ac-text-muted))',
            background: 'hsl(var(--ac-dark-primary) / 0.7)',
            border: `1px solid hsl(var(--ac-mint-primary) / ${connected ? 0.4 : 0.15})`,
            boxShadow: 'inset 0 1px 2px hsl(0 0% 0% / 0.25)',
          }}
        >
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: connected
                ? 'hsl(var(--ac-mint-primary))'
                : 'hsl(var(--ac-text-muted))',
              boxShadow: connected ? '0 0 6px hsl(var(--ac-mint-primary))' : 'none',
            }}
          />
          {connected ? 'Live' : 'Offline'}
        </div>
      </div>

      <div style={{ flex: 1 }} />

      {/* Connection + participant pill */}
      <div
        className="ac-card-flat vtt-topbar-conn"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.375rem 0.75rem',
          flexShrink: 0,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: connected
              ? 'hsl(var(--ac-mint-primary))'
              : 'hsl(var(--ac-text-muted))',
            boxShadow: connected ? '0 0 8px hsl(var(--ac-mint-primary))' : 'none',
          }}
        />
        <span
          className="vtt-topbar-conn-text"
          style={{
            fontSize: '0.75rem',
            color: 'hsl(var(--ac-text-muted))',
            whiteSpace: 'nowrap',
          }}
        >
          {connected ? 'Connected' : 'Disconnected'}
        </span>
        <span
          className="vtt-topbar-conn-count"
          style={{
            marginLeft: '0.375rem',
            paddingLeft: '0.625rem',
            borderLeft: '1px solid hsl(var(--ac-dark-accent))',
            color: 'hsl(var(--ac-text-muted))',
            fontSize: '0.6875rem',
            whiteSpace: 'nowrap',
          }}
        >
          <b style={{ color: 'hsl(var(--ac-text-light))' }}>{participantCount}</b>
          {participantMax != null && (
            <span>
              {' / '}
              {participantMax}
            </span>
          )}
        </span>
      </div>

      {/* User pill */}
      <div
        className="vtt-topbar-user"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          paddingLeft: '0.75rem',
          borderLeft: '1px solid hsl(var(--ac-dark-accent))',
          flexShrink: 0,
        }}
      >
        <div className="vtt-topbar-user-meta" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div
            style={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}
          >
            {displayName}
          </div>
          <div
            style={{
              fontSize: '0.625rem',
              marginTop: 2,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: isGM
                ? 'hsl(var(--ac-gm-gold))'
                : 'hsl(var(--ac-text-muted))',
            }}
          >
            {role}
          </div>
        </div>
        <span className={isGM ? 'vtt-gm-ring' : undefined}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'linear-gradient(135deg, hsl(var(--ac-mint-primary)), hsl(var(--ac-mint-secondary)))',
              color: 'hsl(var(--ac-text-dark))',
              fontFamily: 'var(--ac-font-serif)',
              fontSize: 16,
              fontWeight: 500,
            }}
          >
            {initial}
          </span>
        </span>
      </div>
    </header>
  )
}
